import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDistanceUnit } from '../../hooks/useDistanceUnit'

/**
 * LocationNotification - Display location search status and results
 * 
 * Features:
 * - Shows current search location
 * - Displays search radius
 * - Shows entity count
 * - Formatted coordinates fallback
 * 
 * @idempotent - Pure presentation component
 * @atomic - Single purpose: location status display
 */
const LocationNotification = memo(function LocationNotification({
  userLocation,
  searchLocationName = '',
  radius,
  entityCount = 0,
  isLoading = false,
  size = 'default' // 'default' or 'compact'
}) {
  const { t } = useTranslation()
  const { convertFromKm } = useDistanceUnit()

  if (!userLocation) return null

  const sizeClasses = size === 'compact' 
    ? 'text-xs px-3 py-2'
    : 'text-sm px-2 py-2 sm:px-3 sm:py-3'

  const locationDisplay = searchLocationName || 
    `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`

  return (
    <div className={`${sizeClasses} text-gray-600 bg-gray-50 rounded-md mb-3 sm:mb-4`}>
      <span className="font-medium">{t('map.searchingAround')}</span> {locationDisplay}
      <span className="ml-2 text-gray-500">• {t('map.withinRadius', { radius: convertFromKm(radius, 0), unit: t('unit.distanceAbbr') })}</span>
      {!isLoading && entityCount > 0 && (
        <span className="ml-2 text-gray-500">
          • {t('map.vendorsFound', { count: entityCount })}
        </span>
      )}
    </div>
  )
})

export default LocationNotification
